import type {IssueAgentTask, IssueAssignedAgentInfo} from '../contexts/IssueAgentTasksContext'
import {getTaskTitle} from '@github-ui/agent-sessions/utils/task-helpers'
import {ActionList, ActionMenu, Truncate} from '@primer/react'
import {useCallback, useMemo} from 'react'
import {Breadcrumbs} from '@github-ui/pacer/Breadcrumbs'
import {GitHubAvatar} from '@github-ui/github-avatar'
import styles from './TaskSwitcher.module.css'

type TaskSwitcherProps = {
  tasks: IssueAgentTask[]
  selectedTask: IssueAgentTask
  onSelectTask: (task: IssueAgentTask) => void
  assignedAgents: IssueAssignedAgentInfo[]
}

export function TaskSwitcher({tasks, selectedTask, onSelectTask, assignedAgents}: TaskSwitcherProps) {
  const selectedTitle = getTaskTitle(selectedTask)

  const agentsByID = useMemo(
    () => new Map<number, IssueAssignedAgentInfo>(assignedAgents.map(agent => [agent.id, agent])),
    [assignedAgents],
  )

  const handleSelect = useCallback(
    (task: IssueAgentTask) => {
      if (task.id !== selectedTask.id) {
        onSelectTask(task)
      }
    },
    [selectedTask.id, onSelectTask],
  )

  return tasks.length <= 1 ? (
    <span className={styles.singleItem}>
      <Truncate inline maxWidth={320} title={selectedTitle}>
        {selectedTitle}
      </Truncate>
    </span>
  ) : (
    <div className={styles.multipleItems}>
      <Breadcrumbs aria-label="Agent sessions">
        <Breadcrumbs.ItemWithMenu
          menuOverlay={
            <ActionMenu.Overlay width="medium">
              <ActionList selectionVariant="single">
                <ActionList.Group>
                  <ActionList.GroupHeading>Agent sessions</ActionList.GroupHeading>
                  {tasks.map(task => {
                    const collaborator = task.agent_collaborators?.find(c => agentsByID.has(c.agent_id))
                    const agentInfo = collaborator ? agentsByID.get(collaborator.agent_id) : undefined
                    return (
                      <ActionList.Item
                        key={task.id}
                        selected={task.id === selectedTask.id}
                        onSelect={() => handleSelect(task)}
                      >
                        {agentInfo?.avatar_url && (
                          <ActionList.LeadingVisual>
                            <GitHubAvatar
                              src={agentInfo.avatar_url}
                              size={16}
                              square
                              alt={agentInfo.display_name || 'Agent'}
                            />
                          </ActionList.LeadingVisual>
                        )}
                        {getTaskTitle(task)}
                      </ActionList.Item>
                    )
                  })}
                </ActionList.Group>
              </ActionList>
            </ActionMenu.Overlay>
          }
        >
          {selectedTitle}
        </Breadcrumbs.ItemWithMenu>
      </Breadcrumbs>
    </div>
  )
}
